import { __rest } from "tslib";
import { SocialLogin } from './social-login';
const AUTH_CONNECT_PROVIDERS = ['auth0', 'azure', 'cognito', 'okta', 'onelogin'];
const DEFAULT_SCOPES = {
    auth0: 'openid profile email offline_access',
    azure: 'openid profile email',
    cognito: 'openid profile email',
    okta: 'openid profile email offline_access',
    onelogin: 'openid profile email',
};
const DEFAULT_RESOURCE_URLS = {
    azure: 'https://graph.microsoft.com/v1.0/me',
};
const DEFAULT_AUTHORITY_HOST = 'https://login.microsoftonline.com';
const ensureProtocol = (value) => {
    if (value.startsWith('http://') || value.startsWith('https://')) {
        return value;
    }
    return `https://${value}`;
};
const trimTrailingSlash = (value) => value.replace(/\/+$/, '');
const joinUrl = (base, path) => {
    const sanitizedBase = trimTrailingSlash(base);
    const sanitizedPath = path.replace(/^\/+/, '');
    return `${sanitizedBase}/${sanitizedPath}`;
};
const mergeRecords = (base, overrides) => {
    if (!base && !overrides) {
        return undefined;
    }
    return Object.assign(Object.assign({}, (base !== null && base !== void 0 ? base : {})), (overrides !== null && overrides !== void 0 ? overrides : {}));
};
const validatePreset = (providerId, preset) => {
    if (!preset.clientId) {
        throw new Error(`[authConnect] ${providerId} requires clientId.`);
    }
    if (!preset.redirectUrl) {
        throw new Error(`[authConnect] ${providerId} requires redirectUrl.`);
    }
};
const buildAuth0Config = (preset) => {
    validatePreset('auth0', preset);
    if (!preset.domain) {
        throw new Error('[authConnect] auth0 requires domain.');
    }
    const base = trimTrailingSlash(ensureProtocol(preset.domain));
    return buildConfigFromPreset('auth0', preset, {
        authorizationBaseUrl: joinUrl(base, 'authorize'),
        accessTokenEndpoint: joinUrl(base, 'oauth/token'),
        resourceUrl: joinUrl(base, 'userinfo'),
        logoutUrl: joinUrl(base, 'v2/logout'),
        additionalParameters: preset.audience ? { audience: preset.audience } : undefined,
    });
};
const buildAzureConfig = (preset) => {
    var _a, _b;
    validatePreset('azure', preset);
    const tenantId = (_a = preset.tenantId) !== null && _a !== void 0 ? _a : 'common';
    const authorityHost = trimTrailingSlash(ensureProtocol((_b = preset.authorityHost) !== null && _b !== void 0 ? _b : DEFAULT_AUTHORITY_HOST));
    const base = joinUrl(authorityHost, `${tenantId}/oauth2/v2.0`);
    return buildConfigFromPreset('azure', preset, {
        authorizationBaseUrl: joinUrl(base, 'authorize'),
        accessTokenEndpoint: joinUrl(base, 'token'),
        resourceUrl: DEFAULT_RESOURCE_URLS.azure,
    });
};
const buildCognitoConfig = (preset) => {
    validatePreset('cognito', preset);
    if (!preset.domain) {
        throw new Error('[authConnect] cognito requires domain.');
    }
    const base = trimTrailingSlash(ensureProtocol(preset.domain));
    return buildConfigFromPreset('cognito', preset, {
        authorizationBaseUrl: joinUrl(base, 'oauth2/authorize'),
        accessTokenEndpoint: joinUrl(base, 'oauth2/token'),
        resourceUrl: joinUrl(base, 'oauth2/userInfo'),
        logoutUrl: joinUrl(base, 'logout'),
    });
};
const buildOktaConfig = (preset) => {
    validatePreset('okta', preset);
    if (!preset.issuer) {
        throw new Error('[authConnect] okta requires issuer.');
    }
    const base = trimTrailingSlash(ensureProtocol(preset.issuer));
    return buildConfigFromPreset('okta', preset, {
        authorizationBaseUrl: joinUrl(base, 'v1/authorize'),
        accessTokenEndpoint: joinUrl(base, 'v1/token'),
        resourceUrl: joinUrl(base, 'v1/userinfo'),
        logoutUrl: joinUrl(base, 'v1/logout'),
    });
};
const buildOneLoginConfig = (preset) => {
    validatePreset('onelogin', preset);
    if (!preset.issuer) {
        throw new Error('[authConnect] onelogin requires issuer.');
    }
    const base = trimTrailingSlash(ensureProtocol(preset.issuer));
    return buildConfigFromPreset('onelogin', preset, {
        authorizationBaseUrl: joinUrl(base, 'auth'),
        accessTokenEndpoint: joinUrl(base, 'token'),
        resourceUrl: joinUrl(base, 'me'),
        logoutUrl: joinUrl(base, 'logout'),
    });
};
const buildConfigFromPreset = (providerId, preset, defaults) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const additionalParameters = mergeRecords(defaults.additionalParameters, preset.additionalParameters);
    const additionalResourceHeaders = mergeRecords(defaults.additionalResourceHeaders, preset.additionalResourceHeaders);
    const config = {
        appId: preset.clientId,
        authorizationBaseUrl: (_b = (_a = preset.authorizationBaseUrl) !== null && _a !== void 0 ? _a : defaults.authorizationBaseUrl) !== null && _b !== void 0 ? _b : '',
        accessTokenEndpoint: (_c = preset.accessTokenEndpoint) !== null && _c !== void 0 ? _c : defaults.accessTokenEndpoint,
        redirectUrl: preset.redirectUrl,
        resourceUrl: (_d = preset.resourceUrl) !== null && _d !== void 0 ? _d : defaults.resourceUrl,
        responseType: (_f = (_e = preset.responseType) !== null && _e !== void 0 ? _e : defaults.responseType) !== null && _f !== void 0 ? _f : 'code',
        pkceEnabled: (_h = (_g = preset.pkceEnabled) !== null && _g !== void 0 ? _g : defaults.pkceEnabled) !== null && _h !== void 0 ? _h : true,
        scope: (_k = (_j = preset.scope) !== null && _j !== void 0 ? _j : defaults.scope) !== null && _k !== void 0 ? _k : DEFAULT_SCOPES[providerId],
        additionalParameters,
        additionalResourceHeaders,
        logoutUrl: (_l = preset.logoutUrl) !== null && _l !== void 0 ? _l : defaults.logoutUrl,
        logsEnabled: (_m = preset.logsEnabled) !== null && _m !== void 0 ? _m : defaults.logsEnabled,
    };
    if (!config.authorizationBaseUrl) {
        throw new Error(`[authConnect] ${providerId} authorizationBaseUrl is required.`);
    }
    return config;
};
const buildAuthConnectProviders = (presets) => {
    if (!presets) {
        return {};
    }
    const providers = {};
    if (presets.auth0) {
        providers.auth0 = buildAuth0Config(presets.auth0);
    }
    if (presets.azure) {
        providers.azure = buildAzureConfig(presets.azure);
    }
    if (presets.cognito) {
        providers.cognito = buildCognitoConfig(presets.cognito);
    }
    if (presets.okta) {
        providers.okta = buildOktaConfig(presets.okta);
    }
    if (presets.onelogin) {
        providers.onelogin = buildOneLoginConfig(presets.onelogin);
    }
    return providers;
};
const isAuthConnectProvider = (provider) => AUTH_CONNECT_PROVIDERS.includes(provider);
const isAuthConnectProviderOptions = (options) => isAuthConnectProvider(options.provider);
const isAuthConnectLoginOptions = (options) => isAuthConnectProvider(options.provider);
const mapLoginOptions = (options) => {
    var _a;
    if (isAuthConnectLoginOptions(options)) {
        return {
            provider: 'oauth2',
            options: Object.assign({ providerId: options.provider }, ((_a = options.options) !== null && _a !== void 0 ? _a : {})),
        };
    }
    return options;
};
const mapProviderOptions = (options) => {
    if (isAuthConnectProviderOptions(options)) {
        return {
            provider: 'oauth2',
            providerId: options.provider,
        };
    }
    return options;
};
const mapRefreshOptions = (options) => mapLoginOptions(options);
const mergeOAuth2Configs = (presets, oauth2) => {
    if (!Object.keys(presets).length && !oauth2) {
        return oauth2;
    }
    return Object.assign(Object.assign({}, presets), (oauth2 !== null && oauth2 !== void 0 ? oauth2 : {}));
};
const createAuthConnectClient = (client) => ({
    initialize: async (options) => {
        const { authConnect, oauth2 } = options, rest = __rest(options, ["authConnect", "oauth2"]);
        const presetProviders = buildAuthConnectProviders(authConnect);
        const mergedOauth2 = mergeOAuth2Configs(presetProviders, oauth2);
        const payload = Object.assign(Object.assign({}, rest), (mergedOauth2 ? { oauth2: mergedOauth2 } : {}));
        return client.initialize(payload);
    },
    login: async (options) => {
        if (isAuthConnectProvider(options.provider)) {
            const mapped = mapLoginOptions(options);
            const response = await client.login(mapped);
            return {
                provider: options.provider,
                result: response.result,
            };
        }
        return client.login(options);
    },
    logout: async (options) => client.logout(mapProviderOptions(options)),
    isLoggedIn: async (options) => client.isLoggedIn(mapProviderOptions(options)),
    getAuthorizationCode: async (options) => client.getAuthorizationCode(mapProviderOptions(options)),
    refresh: async (options) => client.refresh(mapRefreshOptions(options)),
    providerSpecificCall: async (options) => client.providerSpecificCall(options),
    getPluginVersion: async () => client.getPluginVersion(),
});
const SocialLoginAuthConnect = createAuthConnectClient(SocialLogin);
export { createAuthConnectClient, SocialLoginAuthConnect };
//# sourceMappingURL=auth-connect.js.map